import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../data/DataProvider';
import { usePortalClient } from './usePortalClient';
import EmptyState from '../shared/components/EmptyState';
import Skeleton from '../shared/components/Skeleton';
import { date, invoiceTotal, money } from '../shared/formatters';
import PortalHeader from './PortalHeader';

const QUOTE_PILLS = { draft: 'draft', sent: 'sent', approved: 'paid', declined: 'overdue', expired: 'cancelled' };

// Portal quotes list (pass 5 §5G) — the client's own quotes. Sent quotes
// are awaiting a decision; approved/declined/expired are historical.
export default function Quotes() {
  const { data } = useData();
  const { client, base } = usePortalClient();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 200);
    return () => clearTimeout(timer);
  }, []);

  const quotes = useMemo(
    () => data.quotes
      .filter((item) => item.client_id === client.id)
      .sort((a, b) => new Date(b.issued_at) - new Date(a.issued_at)),
    [client.id, data.quotes]
  );

  if (loading) {
    return <Skeleton rows={4} />;
  }

  return (
    <>
      <PortalHeader
        eyebrow="Estimates & approvals"
        title="Your quotes"
        description="Approve or decline a quote, or review past ones."
      />

      {!quotes.length ? (
        <EmptyState title="No quotes yet" message="Quotes for proposed work will appear here for your approval." />
      ) : (
        <section className="portal-card no-padding invoice-list">
          {quotes.map((quote) => {
            const display = quote.status === 'sent' ? 'awaiting approval' : quote.status;
            return (
              <Link to={`${base}/quotes/${quote.id}`} key={quote.id}>
                <div>
                  <strong>{quote.number}</strong>
                  <span>{quote.title}</span>
                </div>
                <span>{quote.valid_until ? `Valid until ${date(quote.valid_until)}` : '—'}</span>
                <strong>{money(invoiceTotal(quote))}</strong>
                <span className={`status-pill status-${QUOTE_PILLS[quote.status] || 'draft'}`}>
                  <span className="status-dot" />{display}
                </span>
              </Link>
            );
          })}
        </section>
      )}
    </>
  );
}
